import type {
  TranscriptionService,
  WorkspacePropertiesAdapter,
} from '@pique/core/modules/properties';
import {
  PageSystemPropertyId,
  type SerializedAttendee,
  type SerializedTodoItem,
} from '@pique/core/modules/properties/services/schema';
import type { DocsService, Workspace } from '@toeverything/infra';
import { initEmptyPage } from '@toeverything/infra';
import { atomWithStorage } from 'jotai/utils';

import { corporateTemplate } from './corporate';
import { educationTemplate } from './education';
import {
  createDemoAttendee,
  type DemoActionItem,
  type DemoAttendee,
  type DemoTemplate,
} from './types';

export { corporateTemplate } from './corporate';
export { educationTemplate } from './education';
export type {
  DemoAttendee,
  DemoPreviousNote,
  DemoSocialFeedItem,
  DemoTemplate,
} from './types';

export const DEMO_TEMPLATES: Record<string, DemoTemplate> = {
  corporate: corporateTemplate,
  education: educationTemplate,
};

export const demoModeEnabledAtom = atomWithStorage<boolean>(
  'pique-demo-mode-enabled',
  false
);

export const demoTemplateIdAtom = atomWithStorage<string>(
  'pique-demo-template-id',
  'corporate'
);

export interface CreateDemoPagesOptions {
  workspace: Workspace;
  docsService: DocsService;
  workspacePropertiesAdapter: WorkspacePropertiesAdapter;
  transcriptionService: TranscriptionService;
}

export function getDemoDocTitles(templateId: string): string[] {
  const template = DEMO_TEMPLATES[templateId];
  if (!template) return [];
  return template.previousNotes.map(note => note.title);
}

export function getAllDemoDocTitles(): string[] {
  const allTitles: string[] = [];
  for (const templateId of Object.keys(DEMO_TEMPLATES)) {
    allTitles.push(...getDemoDocTitles(templateId));
  }
  return [...new Set(allTitles)];
}

export async function cleanupDemoDocs(
  options: { workspace: Workspace; docsService: DocsService },
  templateId?: string
): Promise<number> {
  const { workspace, docsService } = options;
  await workspace.engine.waitForRootDocReady();

  const titlesToRemove = templateId
    ? getDemoDocTitles(templateId)
    : getAllDemoDocTitles();

  if (titlesToRemove.length === 0) return 0;

  const titleSet = new Set(titlesToRemove);
  const allDocs = docsService.list.docs$.value;
  const docsToRemove = allDocs.filter(doc => titleSet.has(doc.title$.value));

  for (const doc of docsToRemove) {
    workspace.docCollection.removeDoc(doc.id);
  }

  return docsToRemove.length;
}

function generateId(): string {
  return `demo_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function buildDemoActionItems(
  items: DemoActionItem[],
  allAttendees: DemoAttendee[],
  pageId: string,
  pageTitle: string,
  noteDate: Date
): SerializedTodoItem[] {
  return items.map(item => {
    const attendee = allAttendees.find(a => a.email === item.assignedToEmail);
    const dueDate = new Date(noteDate);
    dueDate.setDate(dueDate.getDate() + 7 - (item.daysOverdue ?? 0));
    return {
      id: generateId(),
      title: item.title,
      pageId,
      pageTitle,
      databaseId: '',
      columnId: '',
      createdAt: noteDate,
      status: (item.status ?? 'To Do') as 'To Do',
      priority: (item.priority ?? 'Medium') as 'Medium',
      progress: 0,
      assignedTo: attendee
        ? [{ id: `demo_${attendee.email.split('@')[0]}`, name: attendee.name, email: attendee.email, avatar: attendee.avatar, present: true, self: false }]
        : [],
      dueDate: dueDate.toISOString().split('T')[0],
      isFocused: false,
    } satisfies SerializedTodoItem;
  });
}

export async function createDemoPreviousNotes(
  options: CreateDemoPagesOptions,
  templateId: string
): Promise<string[]> {
  const template = DEMO_TEMPLATES[templateId];
  if (!template) return [];

  const {
    workspace,
    docsService,
    workspacePropertiesAdapter,
    transcriptionService,
  } = options;
  await workspace.engine.waitForRootDocReady();

  const createdIds: string[] = [];

  for (const note of template.previousNotes) {
    const docRecord = docsService.createDoc({ title: note.title });
    const page = workspace.docCollection.getDoc(docRecord.id);
    if (!page) continue;

    initEmptyPage(page);

    const noteDate = new Date();
    noteDate.setDate(noteDate.getDate() - note.daysAgo);
    const isoDateParts = noteDate.toISOString().split('T');
    const dateString = isoDateParts[0] ?? '';
    workspacePropertiesAdapter.setJournalPageDateString(page.id, dateString);

    const attendees: SerializedAttendee[] = note.attendeeEmails
      .map(email => {
        const demoAttendee = template.attendees.find(a => a.email === email);
        if (!demoAttendee) return null;
        return createDemoAttendee(demoAttendee);
      })
      .filter((a): a is SerializedAttendee => a !== null);

    workspacePropertiesAdapter.setPageAttendees(page.id, attendees);

    if (note.actionItems?.length) {
      const todoItems = buildDemoActionItems(
        note.actionItems,
        template.attendees,
        page.id,
        note.title,
        noteDate
      );
      workspacePropertiesAdapter.setPageActionItems(page.id, todoItems);
    }

    if (note.overview) {
      workspacePropertiesAdapter.setPageOverview(page.id, note.overview);
    }

    if (note.briefSummary) {
      const pageProps = workspacePropertiesAdapter.pageProperties?.[page.id];
      if (pageProps) {
        pageProps.system[PageSystemPropertyId.MeetingPrepSummary].value = {
          briefSummary: note.briefSummary,
          topicsLikelyToComeUp: [],
          selectedAttendeeEmails: note.attendeeEmails,
          sourceDocIds: [],
        };
      }
    }

    workspacePropertiesAdapter.setPageAugmentationStatus(page.id, 'Augmented');

    const transcriptionChunks = note.transcript.map(chunk => {
      const timeParts = chunk.timestamp.split(':').map(Number);
      const mins = timeParts[0] ?? 0;
      const secs = timeParts[1] ?? 0;
      return {
        id: generateId(),
        text: chunk.text,
        timestampMs: (mins * 60 + secs) * 1000,
        speaker: chunk.speakerEmail,
        confidence: 0.95,
        vendor: 'Deepgram' as const,
        isFinal: true,
      };
    });
    transcriptionService.setPageTranscription(page.id, transcriptionChunks);

    const socialFeedItems = note.socialFeedItems.map(item => {
      const attendee = template.attendees.find(
        a => a.email === item.attendeeEmail
      );
      const attendeeEmailParts = item.attendeeEmail.split('@');
      return {
        id: generateId(),
        attendeeEmail: item.attendeeEmail,
        attendeeName:
          attendee?.name ?? attendeeEmailParts[0] ?? item.attendeeEmail,
        text: item.text,
        confidence: item.confidence,
        source: item.category,
      };
    });
    workspacePropertiesAdapter.setPageSocialFeed(page.id, socialFeedItems);

    page.collection.meta.setDocMeta(page.id, {
      title: note.title,
      createDate: noteDate.getTime(),
      updatedDate: noteDate.getTime(),
    });

    createdIds.push(page.id);
  }

  return createdIds;
}
